import { uiText, useUiLanguage } from '../../../../utils/uiTranslation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RecruitmentFilterBar from './components/RecruitmentFilterBar';
import WidgetShell from './components/WidgetShell';
import WidgetLibraryModal from './components/WidgetLibraryModal';
import recruitmentAnalyticsLibrary from './analyticsLibrary';
import { getAnalyticsScreenDefaults, ANALYTICS_SCREEN_IDS } from '../../analyticsScreenDefaults';
import { getAnalyticsToolbarActions } from '../../analyticsToolbar';
import {
  ANALYTICS_FILTER_KEYS,
  ANALYTICS_GROUP_BY_KEYS,
  buildScreenFilterModels,
  canUseMaterializedAnalyticsBundle,
  getSupportedAnalyticsFilterState,
  getLocalizedGroupByOptions,
  normalizeAnalyticsScreenFilterOptions,
  useSessionAnalyticsFilters
} from '../../analyticsFilters';
import {
  createUnavailableAnalyticsBundle,
  mergeAnalyticsWidgetNode,
  useQueriedAnalyticsWidgetData,
  useAnalyticsScreenFilterOptions,
  useAnalyticsWidgetData,
  useSharedAnalyticsDashboard
} from '../../analyticsRtdb';
import { createVisualModel, ANALYTICS_DATA_STATUS } from '../../visuals/visualDataModel';
import { ANALYTICS_GRAPH_TYPES } from '../../visuals/graphCatalog';
import { getCompatibleGraphs, resolveVisualRenderer } from '../../visuals/visualRegistry';
import UnavailableVisual from '../../visuals/UnavailableVisual';
import { atsAnalyticsApi } from '../../../../services/atsAnalyticsService';
import useCanonicalAnalyticsExport from '../../useCanonicalAnalyticsExport';

const ADJACENT_WIDGETS = {
  source_quality_matrix: 'time_to_hire_components',
  time_to_hire_components: 'source_quality_matrix'
};
const SCREEN_DEFAULTS = getAnalyticsScreenDefaults(ANALYTICS_SCREEN_IDS.RECRUITMENT);
const BACKEND_WIDGET_IDS = new Set([
  'stage_conversion_waterfall',
  'drop_off_breakdown',
  'application_to_hire_conversion_rate',
  'time_to_hire_trend',
  'time_to_hire_by_segment',
  'time_to_hire_components',
  'source_quality_matrix',
  'source_rankings',
  'source_trend_over_time',
  'source_to_stage_breakdown',
  'offer_waterfall',
  'offer_risk_summary',
  'headcount_targets_by_tag_group',
  'applicant_traffic_map',
  'job_posting_performance_matrix'
]);
const LIVE_REFRESH_WIDGET_IDS = new Set();
const RECRUITMENT_QUERY_REVISION = 'recruitment-live-metrics-2026-05-16-r2';
const FIXED_GRAPH_WIDGET_IDS = new Set([
  'stage_conversion_waterfall',
  'headcount_targets_by_tag_group'
]);
const HIDDEN_CHART_MENU_WIDGET_IDS = new Set([
  'applicant_traffic_map'
]);
const GROUP_BY_FILTER_KEY = {
  [ANALYTICS_GROUP_BY_KEYS.DEPARTMENT]: ANALYTICS_FILTER_KEYS.DEPARTMENTS,
  [ANALYTICS_GROUP_BY_KEYS.RECRUITER]: ANALYTICS_FILTER_KEYS.RECRUITERS,
  [ANALYTICS_GROUP_BY_KEYS.SOURCE]: ANALYTICS_FILTER_KEYS.SOURCES,
  [ANALYTICS_GROUP_BY_KEYS.TAG]: ANALYTICS_FILTER_KEYS.TAGS,
  [ANALYTICS_GROUP_BY_KEYS.LOCATION]: ANALYTICS_FILTER_KEYS.LOCATIONS
};

const normalizeOptionList = (options = []) =>
  (Array.isArray(options) ? options : [])
    .map((option) => {
      if (typeof option === 'string') {
        return { id: option, label: option };
      }
      return {
        id: String(option?.id || ''),
        label: String(option?.label || option?.id || '')
      };
    })
    .filter((option) => option.id && option.label);

const buildRecruitmentDimensionOptions = (options = {}) => ({
  [ANALYTICS_FILTER_KEYS.DEPARTMENTS]: normalizeOptionList(options[ANALYTICS_FILTER_KEYS.DEPARTMENTS]),
  [ANALYTICS_FILTER_KEYS.ROLES]: normalizeOptionList(options[ANALYTICS_FILTER_KEYS.ROLES]),
  [ANALYTICS_FILTER_KEYS.RECRUITERS]: normalizeOptionList(options[ANALYTICS_FILTER_KEYS.RECRUITERS]),
  [ANALYTICS_FILTER_KEYS.SOURCES]: normalizeOptionList(options[ANALYTICS_FILTER_KEYS.SOURCES]),
  [ANALYTICS_FILTER_KEYS.TAGS]: normalizeOptionList(options[ANALYTICS_FILTER_KEYS.TAGS]),
  [ANALYTICS_FILTER_KEYS.LOCATIONS]: normalizeOptionList(options[ANALYTICS_FILTER_KEYS.LOCATIONS])
});

const getDataBackedGroupByOptions = ({ localizedGroupByOptions = [], supportedGroupBy = [], dimensionOptions = {} }) => {
  const supported = new Set(supportedGroupBy || []);
  return localizedGroupByOptions.filter((option) => {
    if (!supported.has(option.id)) return false;
    const filterKey = GROUP_BY_FILTER_KEY[option.id];
    return filterKey ? Boolean((dimensionOptions[filterKey] || []).length) : true;
  });
};

const toCount = (value) => Math.max(0, Number(value || 0));
const percent = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);
const hasToken = (item, tokens = []) => {
  const text = `${item?.id || ''} ${item?.label || ''}`.toLowerCase();
  return tokens.some((token) => text.includes(token));
};

const sumLegacyOfferOutcome = (stages = [], exits = [], tokens = []) => {
  const stageCount = stages
    .filter((stage) => hasToken(stage, tokens))
    .reduce((sum, stage) => sum + toCount(stage.count), 0);
  const exitCount = exits
    .filter((exit) => hasToken(exit, tokens))
    .reduce((sum, exit) => sum + toCount(exit.count), 0);
  return Math.max(stageCount, exitCount);
};

const sanitizeOfferWaterfallDataset = (dataset) => {
  if (!dataset || !Array.isArray(dataset.stages)) return dataset;

  const legacyStages = dataset.stages;
  const legacyExits = Array.isArray(dataset.exits) ? dataset.exits : [];
  const sentStage =
    legacyStages.find((stage) => ['extended', 'offer_sent', 'offers_sent'].includes(String(stage.id || '').toLowerCase())) ||
    legacyStages.find((stage) => hasToken(stage, ['offer'])) ||
    legacyStages[0] ||
    {};
  const acceptedStage =
    legacyStages.find((stage) => String(stage.id || '').toLowerCase() === 'accepted') ||
    legacyStages.find((stage) => hasToken(stage, ['accepted'])) ||
    {};
  const acceptedCount = toCount(acceptedStage.count);
  const declinedCount = sumLegacyOfferOutcome(legacyStages, legacyExits, ['declined', 'rejected', 'reject']);
  const expiredCount = sumLegacyOfferOutcome(legacyStages, legacyExits, ['expired']);
  const sentCount = Math.max(toCount(sentStage.count), acceptedCount + declinedCount + expiredCount);
  const normalizedSentCount = sentCount || toCount(sentStage.count) || acceptedCount;

  return {
    ...dataset,
    stages: [
      {
        ...sentStage,
        id: 'extended',
        label: 'Offer(s) sent',
        count: normalizedSentCount,
        conversionRate: 100
      },
      {
        ...acceptedStage,
        id: 'accepted',
        label: 'Accepted',
        count: acceptedCount,
        conversionRate: percent(acceptedCount, normalizedSentCount)
      }
    ],
    exits: [
      {
        id: 'offer_rejected_declined',
        sourceStageId: 'extended',
        label: 'Rejected / declined',
        count: declinedCount,
        rate: percent(declinedCount, normalizedSentCount)
      },
      {
        id: 'offer_expired',
        sourceStageId: 'extended',
        label: 'Expired',
        count: expiredCount,
        rate: percent(expiredCount, normalizedSentCount)
      }
    ]
  };
};

const sanitizeOfferWaterfallBundle = (bundle) => {
  if (!bundle?.datasets) return bundle;
  return {
    ...bundle,
    datasets: Object.fromEntries(
      Object.entries(bundle.datasets).map(([graphId, dataset]) => [
        graphId,
        Array.isArray(dataset?.stages) ? sanitizeOfferWaterfallDataset(dataset) : dataset
      ])
    )
  };
};

const COPY = {
  en: {
    library: {
      title: 'Widget Library',
      subtitle: 'Add any recruitment widget below. Live analytics populate automatically as data becomes available.',
      close: 'Close',
      add: 'Add widget',
      added: 'Added',
      exampleDataReady: 'Data available',
      exampleDataMissing: 'Not available yet',
      graphCount: (count) => `${count} graph${count === 1 ? '' : 's'}`,
      categories: {
        funnel_conversion: 'Funnel & Conversion',
        time_velocity: 'Time & Velocity',
        source_effectiveness: 'Source Effectiveness',
        offer_intelligence: 'Offer Intelligence',
        hiring_goals_actuals: 'Hiring Goals vs Actuals',
        geography: 'Geography',
        job_posting_performance: 'Job Posting Performance'
      }
    },
    menu: {
      settings: 'Widget settings',
      chartType: 'Chart type',
      groupBy: 'Group by',
      cardVariant: 'Card variant',
      cardSize: 'Card size',
      resize: 'Resize widget',
      configureTargets: 'Configure targets',
      compact: 'Compact',
      medium: 'Medium',
      'three-quarter': 'Three quarter',
      large: 'Large',
      'data-forward': 'Data forward',
      contextual: 'Contextual',
      executive: 'Executive',
      unavailable: 'No compatible data is available for this graph yet.',
      graphs: {
        [ANALYTICS_GRAPH_TYPES.FUNNEL_WATERFALL]: 'Funnel waterfall',
        [ANALYTICS_GRAPH_TYPES.PIPELINE_FLOW]: 'Pipeline flow',
        [ANALYTICS_GRAPH_TYPES.FUNNEL]: 'Funnel',
        [ANALYTICS_GRAPH_TYPES.HORIZONTAL_BAR]: 'Horizontal bar',
        [ANALYTICS_GRAPH_TYPES.SANKEY_FLOW]: 'Sankey flow',
        [ANALYTICS_GRAPH_TYPES.LINE_OVER_TIME]: 'Line over time',
        [ANALYTICS_GRAPH_TYPES.AREA_CHART]: 'Area chart',
        [ANALYTICS_GRAPH_TYPES.MULTI_LINE]: 'Multi-line',
        [ANALYTICS_GRAPH_TYPES.BAR_OVER_TIME]: 'Bar over time',
        [ANALYTICS_GRAPH_TYPES.STACKED_AREA]: 'Stacked area',
        [ANALYTICS_GRAPH_TYPES.GROUPED_BAR]: 'Grouped bar',
        [ANALYTICS_GRAPH_TYPES.STACKED_BAR]: 'Stacked bar',
        [ANALYTICS_GRAPH_TYPES.STACKED_HORIZONTAL_BAR]: 'Stacked horizontal bar',
        [ANALYTICS_GRAPH_TYPES.SCATTER]: 'Scatter plot',
        [ANALYTICS_GRAPH_TYPES.BUBBLE_CHART]: 'Bubble chart',
        [ANALYTICS_GRAPH_TYPES.HEATMAP]: 'Heatmap',
        [ANALYTICS_GRAPH_TYPES.TABLE]: 'Table',
        [ANALYTICS_GRAPH_TYPES.CONDITIONAL_TABLE]: 'Heatmap',
        [ANALYTICS_GRAPH_TYPES.KPI_CARD]: 'KPI card',
        [ANALYTICS_GRAPH_TYPES.OFFER_SIGNAL_CARD]: 'Offer risk summary',
        [ANALYTICS_GRAPH_TYPES.SOURCE_RANKINGS]: 'Source rankings',
        [ANALYTICS_GRAPH_TYPES.KPI_SPARKLINE]: 'KPI card with sparkline',
        [ANALYTICS_GRAPH_TYPES.GAUGE]: 'Gauge',
        [ANALYTICS_GRAPH_TYPES.GOAL_TRACK]: 'Goal vs actual',
        [ANALYTICS_GRAPH_TYPES.GOAL_LABEL_TREND]: 'Goal trend by label',
        [ANALYTICS_GRAPH_TYPES.RADIAL_RINGS]: 'Goal rings',
        [ANALYTICS_GRAPH_TYPES.PROGRESS_BARS]: 'Progress bars',
        [ANALYTICS_GRAPH_TYPES.BULLET_CHART]: 'Bullet chart',
        [ANALYTICS_GRAPH_TYPES.BOX_PLOT]: 'Box plot',
        [ANALYTICS_GRAPH_TYPES.VIOLIN_PLOT]: 'Violin plot',
        [ANALYTICS_GRAPH_TYPES.HISTOGRAM]: 'Histogram',
        [ANALYTICS_GRAPH_TYPES.WORLD_CHOROPLETH]: 'World map',
        [ANALYTICS_GRAPH_TYPES.REGIONAL_MAP]: 'Region map',
        [ANALYTICS_GRAPH_TYPES.CITY_DOT_MAP]: 'Country city map'
      }
    },
    trafficMap: {
      ariaLabel: 'Applicant traffic geographic view',
      loading: 'Loading applicant geography...',
      error: 'Map data is unavailable right now.',
      hoverHint: 'Hover a country to inspect it',
      views: [
        { id: 'world', label: 'World' },
        { id: 'region', label: 'Region' },
        { id: 'country', label: 'Country' }
      ]
    },
    widgetUnavailable: {
      time_to_hire_components: 'Needs at least one accepted or hired candidate with both application created date and offer accepted date, or a start date fallback, inside the selected period.'
    },
    targetModalTitle: 'Quarterly headcount targets',
    targetModalSubtitle: 'Set department hiring goals for the current quarter. These targets use the same shared analytics target path as Executive Overview.',
    targetDepartmentLabel: 'Department',
    targetValueLabel: 'Target hires',
    targetAddDepartment: 'Add department',
    targetSave: 'Save targets',
    targetCancel: 'Cancel',
    targetLoading: 'Loading targets...',
    targetSaving: 'Saving...',
    targetEmptyDepartment: 'Department name',
    targetError: 'Could not save headcount targets.',
    targetMissingCompany: 'Could not save headcount targets because no company is selected.'
  },
  ar: {
    library: {
      title: 'مكتبة العناصر',
      subtitle: 'أضف أي عنصر من عناصر تحليلات التوظيف أدناه. يتم تعبئة التحليلات تلقائياً عند توفر البيانات.',
      close: 'إغلاق',
      add: 'إضافة عنصر',
      added: 'مضاف',
      exampleDataReady: 'البيانات التجريبية جاهزة',
      exampleDataMissing: 'البيانات غير متاحة',
      graphCount: (count) => `${count} رسم${count === 1 ? '' : ''}`,
      categories: {
        funnel_conversion: 'المسار والتحويل',
        time_velocity: 'الوقت والسرعة',
        source_effectiveness: 'فعالية المصادر',
        offer_intelligence: 'ذكاء العروض',
        hiring_goals_actuals: 'الأهداف مقابل الفعلي',
        geography: 'الجغرافيا',
        job_posting_performance: 'أداء نشر الوظائف'
      }
    },
    menu: {
      settings: 'إعدادات العنصر',
      chartType: 'نوع الرسم',
      groupBy: 'التجميع حسب',
      cardVariant: 'نمط البطاقة',
      cardSize: 'حجم البطاقة',
      resize: 'تغيير حجم العنصر',
      configureTargets: 'تكوين الأهداف',
      compact: 'مصغر',
      medium: 'متوسط',
      'three-quarter': 'ثلاثة أرباع',
      large: 'كبير',
      'data-forward': 'يركز على البيانات',
      contextual: 'تشغيلي',
      executive: 'تنفيذي',
      unavailable: 'لا توجد بيانات متوافقة لهذا الرسم حتى الآن.',
      graphs: {
        [ANALYTICS_GRAPH_TYPES.FUNNEL_WATERFALL]: 'شلال المسار',
        [ANALYTICS_GRAPH_TYPES.PIPELINE_FLOW]: 'تدفق المسار',
        [ANALYTICS_GRAPH_TYPES.FUNNEL]: 'مسار',
        [ANALYTICS_GRAPH_TYPES.HORIZONTAL_BAR]: 'شريط أفقي',
        [ANALYTICS_GRAPH_TYPES.SANKEY_FLOW]: 'تدفق سانكي',
        [ANALYTICS_GRAPH_TYPES.LINE_OVER_TIME]: 'خط زمني',
        [ANALYTICS_GRAPH_TYPES.AREA_CHART]: 'مخطط مساحي',
        [ANALYTICS_GRAPH_TYPES.MULTI_LINE]: 'خطوط متعددة',
        [ANALYTICS_GRAPH_TYPES.BAR_OVER_TIME]: 'أشرطة زمنية',
        [ANALYTICS_GRAPH_TYPES.STACKED_AREA]: 'مساحة متراكمة',
        [ANALYTICS_GRAPH_TYPES.GROUPED_BAR]: 'شريط مجمع',
        [ANALYTICS_GRAPH_TYPES.STACKED_BAR]: 'شريط متراكم',
        [ANALYTICS_GRAPH_TYPES.STACKED_HORIZONTAL_BAR]: 'شريط أفقي متراكم',
        [ANALYTICS_GRAPH_TYPES.SCATTER]: 'مخطط مبعثر',
        [ANALYTICS_GRAPH_TYPES.BUBBLE_CHART]: 'مخطط فقاعي',
        [ANALYTICS_GRAPH_TYPES.HEATMAP]: 'خريطة حرارية',
        [ANALYTICS_GRAPH_TYPES.TABLE]: 'جدول',
        [ANALYTICS_GRAPH_TYPES.CONDITIONAL_TABLE]: 'خريطة حرارية',
        [ANALYTICS_GRAPH_TYPES.KPI_CARD]: 'بطاقة مؤشر',
        [ANALYTICS_GRAPH_TYPES.OFFER_SIGNAL_CARD]: 'ملخص مخاطر العروض',
        [ANALYTICS_GRAPH_TYPES.SOURCE_RANKINGS]: 'تصنيفات المصادر',
        [ANALYTICS_GRAPH_TYPES.KPI_SPARKLINE]: 'بطاقة مؤشر مع شرارة',
        [ANALYTICS_GRAPH_TYPES.GAUGE]: 'مقياس',
        [ANALYTICS_GRAPH_TYPES.GOAL_TRACK]: 'الهدف مقابل الفعلي',
        [ANALYTICS_GRAPH_TYPES.GOAL_LABEL_TREND]: 'اتجاه الهدف حسب الوسم',
        [ANALYTICS_GRAPH_TYPES.RADIAL_RINGS]: 'حلقات الأهداف',
        [ANALYTICS_GRAPH_TYPES.PROGRESS_BARS]: 'أشرطة التقدم',
        [ANALYTICS_GRAPH_TYPES.BULLET_CHART]: 'مخطط الهدف',
        [ANALYTICS_GRAPH_TYPES.BOX_PLOT]: 'مخطط صندوقي',
        [ANALYTICS_GRAPH_TYPES.VIOLIN_PLOT]: 'مخطط كماني',
        [ANALYTICS_GRAPH_TYPES.HISTOGRAM]: 'مدرج تكراري',
        [ANALYTICS_GRAPH_TYPES.WORLD_CHOROPLETH]: 'خريطة العالم',
        [ANALYTICS_GRAPH_TYPES.REGIONAL_MAP]: 'خريطة المناطق',
        [ANALYTICS_GRAPH_TYPES.CITY_DOT_MAP]: 'خريطة المدن'
      }
    },
    trafficMap: {
      ariaLabel: 'عرض جغرافي لحركة المتقدمين',
      loading: 'جارٍ تحميل الخريطة الجغرافية...',
      error: 'بيانات الخريطة غير متاحة الآن.',
      hoverHint: 'مرر فوق دولة لعرض اسمها',
      views: [
        { id: 'world', label: 'العالم' },
        { id: 'region', label: 'المنطقة' },
        { id: 'country', label: 'الدولة' }
      ]
    },
    widgetUnavailable: {
      time_to_hire_components: 'يتطلب هذا الرسم مرشحاً واحداً على الأقل تم قبول عرضه أو توظيفه مع تاريخ إنشاء الطلب وتاريخ قبول العرض، أو تاريخ بدء كبديل، ضمن الفترة المحددة.'
    },
    targetModalTitle: 'أهداف التوظيف الربع سنوية',
    targetModalSubtitle: 'حدد أهداف التوظيف لكل قسم للربع الحالي. تستخدم هذه الأهداف نفس مسار بيانات التحليلات المستخدم في النظرة التنفيذية.',
    targetDepartmentLabel: 'القسم',
    targetValueLabel: 'هدف التوظيف',
    targetAddDepartment: 'إضافة قسم',
    targetSave: 'حفظ الأهداف',
    targetCancel: 'إلغاء',
    targetLoading: 'جاري تحميل الأهداف...',
    targetSaving: 'جاري الحفظ...',
    targetEmptyDepartment: 'اسم القسم',
    targetError: 'تعذر حفظ أهداف التوظيف.',
    targetMissingCompany: 'تعذر حفظ أهداف التوظيف لأنه لم يتم تحديد شركة.'
  }
};

const normalizeDraftDepartmentId = (label, index) =>
  String(label || `department-${index + 1}`)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || `department_${index + 1}`;

const normalizeHeadcountKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const normalizeTargetRows = (departments = []) =>
  (Array.isArray(departments) ? departments : [])
    .map((department, index) => {
      const label = String(department?.label || department?.department || department?.name || '').trim();
      return {
        id: String(department?.id || normalizeDraftDepartmentId(label, index)),
        label,
        target: Math.max(0, Number(department?.target || 0))
      };
    })
    .filter((department) => department.label);

const createSeedTargetRows = (departments = []) => {
  const rows = normalizeTargetRows(departments);
  return rows.length ? rows : [{ id: 'department_1', label: '', target: 0 }];
};

const getHeadcountPlanPeriods = (dataset = {}, plan = {}) => {
  const datasetPeriods = Array.isArray(dataset.periods) ? dataset.periods.filter((period) => period?.key) : [];
  if (datasetPeriods.length) return datasetPeriods;
  if (plan?.periodKey) {
    return [{ key: plan.periodKey, label: plan.periodLabel || plan.periodKey }];
  }
  return [];
};

const getPlanDaysRemaining = (plan = {}) => {
  const expiresAt = plan?.expiresAt ? new Date(plan.expiresAt).getTime() : 0;
  if (!Number.isFinite(expiresAt) || expiresAt <= 0) return 0;
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / 86400000));
};

const buildExistingHeadcountIndex = (items = []) => {
  const index = new Map();
  items.forEach((item) => {
    [item?.id, item?.label].forEach((value) => {
      const key = normalizeHeadcountKey(value);
      if (key && !index.has(key)) {
        index.set(key, item);
      }
    });
  });
  return index;
};

const createHeadcountHistory = ({ existingItem = {}, periods = [], target = 0, actual = 0 }) => {
  const existingHistory = Array.isArray(existingItem.history) ? existingItem.history : [];
  const historyByPeriod = new Map(existingHistory.map((entry) => [String(entry?.periodKey || ''), entry]));
  if (!periods.length) {
    return [{ periodKey: 'current', target, actual }];
  }
  return periods.map((period, index) => {
    const existingEntry = historyByPeriod.get(String(period.key || '')) || {};
    return {
      periodKey: period.key,
      target,
      actual: Math.max(0, Number(existingEntry.actual ?? (index === periods.length - 1 ? actual : 0)) || 0)
    };
  });
};

const mergeHeadcountPlanIntoDataset = (dataset = {}, plan = {}) => {
  const targetRows = normalizeTargetRows(plan?.departments || []);
  if (!targetRows.length) {
    return {
      ...dataset,
      shape: 'goal_progress',
      periods: getHeadcountPlanPeriods(dataset, plan),
      items: []
    };
  }

  const existingItems = Array.isArray(dataset.items) ? dataset.items : [];
  const existingIndex = buildExistingHeadcountIndex(existingItems);
  const periods = getHeadcountPlanPeriods(dataset, plan);
  const planKeys = new Set(targetRows.flatMap((row) => [
    normalizeHeadcountKey(row.id),
    normalizeHeadcountKey(row.label)
  ]));

  const planItems = targetRows.map((row, index) => {
    const existingItem =
      existingIndex.get(normalizeHeadcountKey(row.id)) ||
      existingIndex.get(normalizeHeadcountKey(row.label)) ||
      {};
    const target = Math.max(0, Number(row.target || 0));
    const actual = Math.max(0, Number(existingItem.actual || 0));
    const pipeline = Math.max(0, Number(existingItem.pipeline || 0));

    return {
      ...existingItem,
      id: normalizeDraftDepartmentId(row.id || row.label, index),
      label: row.label,
      target,
      actual,
      pipeline,
      percentComplete: target ? Math.round((actual / target) * 1000) / 10 : 0,
      daysRemaining: Number.isFinite(Number(existingItem.daysRemaining))
        ? Number(existingItem.daysRemaining)
        : getPlanDaysRemaining(plan),
      history: createHeadcountHistory({ existingItem, periods, target, actual })
    };
  });

  const unconfiguredItems = existingItems.filter((item) => {
    const idKey = normalizeHeadcountKey(item?.id);
    const labelKey = normalizeHeadcountKey(item?.label);
    return !planKeys.has(idKey) && !planKeys.has(labelKey);
  });

  return {
    ...dataset,
    title: dataset.title || 'Hiring Goals vs Actuals',
    shape: 'goal_progress',
    periods,
    items: [...planItems, ...unconfiguredItems]
  };
};

const mergeHeadcountPlanIntoBundle = (bundle, plan, planLoaded = true) => {
  const targetRows = normalizeTargetRows(plan?.departments || []);
  const currentDatasets = bundle?.datasets || {};
  if (!planLoaded || !targetRows.length) {
    return {
      ...bundle,
      dataStatus: ANALYTICS_DATA_STATUS.UNSUPPORTED,
      datasets: {},
      supportedGroupBy: [ANALYTICS_GROUP_BY_KEYS.DEPARTMENT]
    };
  }
  const sourceDataset =
    currentDatasets[ANALYTICS_GRAPH_TYPES.GOAL_TRACK] ||
    Object.values(currentDatasets).find((dataset) => dataset?.shape === 'goal_progress') ||
    {};

  return {
    ...bundle,
    title: bundle?.title || 'Hiring Goals vs Actuals',
    dataStatus: ANALYTICS_DATA_STATUS.READY,
    datasets: {
      ...currentDatasets,
      [ANALYTICS_GRAPH_TYPES.GOAL_TRACK]: mergeHeadcountPlanIntoDataset(sourceDataset, plan)
    },
    supportedGroupBy: [ANALYTICS_GROUP_BY_KEYS.DEPARTMENT]
  };
};

const formatHeadcountTargetError = (copy, error) => {
  const detail = String(error?.message || error?.body?.detail || '').trim();
  return detail ? `${copy.targetError} ${detail}` : copy.targetError;
};

const HeadcountTargetModal = ({
  copy,
  open,
  rows,
  periodLabel,
  loading,
  saving,
  error,
  isRTL,
  onClose,
  onSave,
  onAddRow,
  onRemoveRow,
  onChangeRow
}) => {
  useUiLanguage();
  if (!open) return null;

  return (
    <div className="executive-overview-modal-backdrop">
      <section className={`executive-overview-modal executive-overview-target-modal${isRTL ? ' rtl' : ''}`}>
        <div className="executive-overview-modal-head">
          <div>
            <h3>{copy.targetModalTitle}</h3>
            <p>{copy.targetModalSubtitle}</p>
            {periodLabel ? <p>{periodLabel}</p> : null}
          </div>
          <button type="button" className="executive-overview-modal-close" onClick={onClose}>
            {copy.targetCancel}
          </button>
        </div>

        <div className="executive-overview-target-form">
          {loading ? (
            <div className="executive-overview-empty-state">{copy.targetLoading}</div>
          ) : (
            <>
              <div className="executive-overview-target-form-head">
                <span>{copy.targetDepartmentLabel}</span>
                <span>{copy.targetValueLabel}</span>
                <span aria-hidden="true" />
              </div>

              <div className="executive-overview-target-form-rows">
                {rows.map((row, index) => (
                  <div key={row.id || index} className="executive-overview-target-form-row">
                    <input
                      type="text"
                      value={row.label}
                      placeholder={copy.targetEmptyDepartment}
                      onChange={(event) => onChangeRow(index, { label: event.target.value })}
                    />
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={row.target}
                      onChange={(event) => onChangeRow(index, { target: event.target.value })}
                    />
                    <button
                      type="button"
                      className="executive-overview-target-row-remove"
                      onClick={() => onRemoveRow(index)}
                      aria-label={uiText("Remove department")}
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                ))}
              </div>

              {error ? <div className="executive-overview-target-error">{uiText(error)}</div> : null}

              <div className="executive-overview-target-form-actions">
                <button type="button" className="executive-overview-target-secondary" onClick={onAddRow}>
                  <i className="fa-solid fa-plus" />
                  <span>{copy.targetAddDepartment}</span>
                </button>
                <button type="button" className="executive-overview-target-primary" onClick={onSave} disabled={saving}>
                  {saving ? copy.targetSaving : copy.targetSave}
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

const AnalyticsRecruitment = ({ isRTL = false, companyUID = '', analyticsActorId = 'system' }) => {
  const copy = isRTL ? COPY.ar : COPY.en;
  const [libraryOpen, setLibraryOpen] = useState(false);
  const { filterState, setFilterValue } = useSessionAnalyticsFilters({ screenId: SCREEN_DEFAULTS.screenId });
  const [headcountSettingsOpen, setHeadcountSettingsOpen] = useState(false);
  const [headcountSettingsLoading, setHeadcountSettingsLoading] = useState(false);
  const [headcountSettingsSaving, setHeadcountSettingsSaving] = useState(false);
  const [headcountSettingsError, setHeadcountSettingsError] = useState('');
  const [headcountPlan, setHeadcountPlan] = useState(null);
  const [headcountPlanLoaded, setHeadcountPlanLoaded] = useState(!companyUID);
  const [headcountTargetRows, setHeadcountTargetRows] = useState([{ id: 'department_1', label: '', target: 0 }]);
  const [headcountRevision, setHeadcountRevision] = useState(0);
  const {
    visibleWidgetIds,
    widgetPrefs,
    mutateDashboardState,
    resetDashboardState,
    dashboardReady
  } = useSharedAnalyticsDashboard({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    defaultVisibleWidgetIds: SCREEN_DEFAULTS.defaultVisibleWidgetIds,
    defaultWidgetPrefs: SCREEN_DEFAULTS.defaultWidgetPrefs,
    locked: SCREEN_DEFAULTS.locked,
      updatedBy: analyticsActorId
  });
  const { screenFilterNode: remoteFilterNode, loading: remoteFilterLoading } = useAnalyticsScreenFilterOptions({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId
  });
  const { widgetNodes, loading: widgetLoading } = useAnalyticsWidgetData({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    widgetIds: dashboardReady ? visibleWidgetIds : []
  });
  const fallbackFilterOptions = useMemo(() => ({}), []);
  const recruitmentDimensionOptions = useMemo(
    () => buildRecruitmentDimensionOptions(remoteFilterNode?.options || {}),
    [remoteFilterNode]
  );
  const filterOptions = useMemo(
    () =>
      normalizeAnalyticsScreenFilterOptions({
        screenId: SCREEN_DEFAULTS.screenId,
        isRTL,
        remoteOptions: remoteFilterNode?.options || {},
        fallbackOptions: fallbackFilterOptions
      }),
    [fallbackFilterOptions, isRTL, remoteFilterNode]
  );
  const filterModels = useMemo(
    () =>
      buildScreenFilterModels({
        screenId: SCREEN_DEFAULTS.screenId,
        isRTL,
        filterState,
        filterOptions
      }),
    [filterOptions, filterState, isRTL]
  );
  const queryFilterState = useMemo(() => {
    const visibleFilterIds = new Set(filterModels.map((filter) => filter.id));
    return Object.entries(filterState).reduce((accumulator, [filterId, value]) => {
      if (visibleFilterIds.has(filterId)) {
        accumulator[filterId] = value;
      }
      return accumulator;
    }, {});
  }, [filterModels, filterState]);
  const localizedGroupByOptions = useMemo(() => getLocalizedGroupByOptions(isRTL), [isRTL]);
  const departmentSeedOptions = useMemo(
    () => (recruitmentDimensionOptions[ANALYTICS_FILTER_KEYS.DEPARTMENTS] || []).map((department) => ({ id: department.id, label: department.label, target: 0 })),
    [recruitmentDimensionOptions]
  );
  const departmentSeedOptionsRef = useRef([]);
  useEffect(() => {
    if (!headcountSettingsOpen) {
      departmentSeedOptionsRef.current = departmentSeedOptions;
    }
  }, [departmentSeedOptions, headcountSettingsOpen]);
  useEffect(() => {
    if (!companyUID) {
      setHeadcountPlan(null);
      setHeadcountPlanLoaded(true);
      setHeadcountTargetRows([{ id: 'department_1', label: '', target: 0 }]);
      return undefined;
    }

    let cancelled = false;
    setHeadcountPlanLoaded(false);
    atsAnalyticsApi
      .getHeadcountTargets(companyUID)
      .then((response) => {
        if (cancelled) return;
        setHeadcountPlan(response?.plan || null);
        setHeadcountPlanLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setHeadcountPlan(null);
          setHeadcountPlanLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [companyUID, headcountRevision]);

  useEffect(() => {
    if (!headcountSettingsOpen || !companyUID) return undefined;

    let cancelled = false;
    setHeadcountSettingsLoading(true);
    setHeadcountSettingsError('');

    atsAnalyticsApi
      .getHeadcountTargets(companyUID)
      .then((response) => {
        if (cancelled) return;
        const plan = response?.plan || null;
        const planRows = normalizeTargetRows(plan?.departments || []);
        setHeadcountPlan(plan);
        setHeadcountPlanLoaded(true);
        setHeadcountTargetRows(planRows.length ? planRows : createSeedTargetRows(departmentSeedOptionsRef.current || []));
        setHeadcountSettingsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setHeadcountPlan(null);
        setHeadcountPlanLoaded(true);
        setHeadcountSettingsError(formatHeadcountTargetError(copy, error));
        setHeadcountTargetRows(createSeedTargetRows(departmentSeedOptionsRef.current || []));
        setHeadcountSettingsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyUID, copy, headcountSettingsOpen]);

  const openHeadcountSettings = useCallback(() => {
    setHeadcountSettingsError('');
    setHeadcountSettingsLoading(Boolean(companyUID));
    setHeadcountTargetRows([{ id: 'department_1', label: '', target: 0 }]);
    setHeadcountSettingsOpen(true);
  }, [companyUID]);

  const updateHeadcountTargetRow = useCallback((index, patch) => {
    setHeadcountTargetRows((current) =>
      current.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        const nextLabel = patch.label !== undefined ? patch.label : row.label;
        return {
          ...row,
          ...patch,
          id: patch.label !== undefined ? normalizeDraftDepartmentId(nextLabel, rowIndex) : row.id,
          target: patch.target !== undefined ? Math.max(0, Number(patch.target || 0)) : row.target
        };
      })
    );
  }, []);

  const addHeadcountTargetRow = useCallback(() => {
    setHeadcountTargetRows((current) => [
      ...current,
      { id: `department_${current.length + 1}`, label: '', target: 0 }
    ]);
  }, []);

  const removeHeadcountTargetRow = useCallback((index) => {
    setHeadcountTargetRows((current) => {
      const nextRows = current.filter((_, rowIndex) => rowIndex !== index);
      return nextRows.length ? nextRows : [{ id: 'department_1', label: '', target: 0 }];
    });
  }, []);

  const saveHeadcountTargets = useCallback(async () => {
    if (headcountSettingsSaving) return;
    if (!companyUID) {
      setHeadcountSettingsError(copy.targetMissingCompany || copy.targetError);
      return;
    }

    setHeadcountSettingsSaving(true);
    setHeadcountSettingsError('');

    try {
      const departments = headcountTargetRows
        .map((row, index) => ({
          id: normalizeDraftDepartmentId(row.label || row.id, index),
          label: String(row.label || '').trim(),
          target: Math.max(0, Number(row.target || 0))
        }))
        .filter((row) => row.label);

      const response = await atsAnalyticsApi.saveHeadcountTargets(companyUID, {
        periodKey: headcountPlan?.periodKey,
        departments
      });
      const nextPlan = response?.plan || null;
      setHeadcountPlan(nextPlan);
      setHeadcountPlanLoaded(true);
      setHeadcountTargetRows(createSeedTargetRows(nextPlan?.departments || departments));
      setHeadcountRevision(Date.now());
      setHeadcountSettingsOpen(false);
    } catch (error) {
      setHeadcountSettingsError(formatHeadcountTargetError(copy, error));
    } finally {
      setHeadcountSettingsSaving(false);
    }
  }, [companyUID, copy, headcountPlan, headcountSettingsSaving, headcountTargetRows]);

  const widgetEntries = useMemo(
    () =>
      visibleWidgetIds.map((id) => recruitmentAnalyticsLibrary.widgets.find((widget) => widget.id === id)).filter(Boolean),
    [visibleWidgetIds]
  );

  const widgetRuntimeConfig = useMemo(
    () =>
      Object.fromEntries(
        widgetEntries.map((entry) => {
          const prefs = widgetPrefs[entry.id] || {};
          const activeGroupBy = entry.id === 'headcount_targets_by_tag_group'
            ? ANALYTICS_GROUP_BY_KEYS.DEPARTMENT
            : prefs.selectorState?.groupBy || entry.defaultGroupBy || '';
          const effectiveFilters = getSupportedAnalyticsFilterState({
            filterState: queryFilterState,
            supportedFilters: entry.supportedFilters || [],
            groupBy: activeGroupBy
          });
          const materializedGroupBy = Array.isArray(widgetNodes[entry.id]?.supportedGroupBy)
            ? widgetNodes[entry.id].supportedGroupBy
            : [];
          const canUseMaterializedBundle = canUseMaterializedAnalyticsBundle({
            screenId: SCREEN_DEFAULTS.screenId,
            filterState: queryFilterState,
            supportedFilters: entry.supportedFilters || [],
            supportsGroupBy: entry.supportsGroupBy,
            activeGroupBy,
            defaultGroupBy: entry.defaultGroupBy || '',
            materializedGroupBy
          });

          return [entry.id, {
            prefs,
            activeGroupBy,
            effectiveFilters,
            canUseMaterializedBundle
          }];
        })
      ),
    [queryFilterState, widgetEntries, widgetNodes, widgetPrefs]
  );

  const { widgetNodes: queriedWidgetNodes, loading: queriedLoading } = useQueriedAnalyticsWidgetData({
    companyUID,
    screenId: SCREEN_DEFAULTS.screenId,
    widgetQueryMap: Object.fromEntries(
      widgetEntries.map((entry) => {
        const runtime = widgetRuntimeConfig[entry.id] || {};
        const hasMaterializedNode = Boolean(widgetNodes[entry.id]);
        const shouldRefreshLive = LIVE_REFRESH_WIDGET_IDS.has(entry.id);
        return [entry.id, {
          enabled: Boolean(companyUID) && (
            shouldRefreshLive ||
            (entry.id === 'headcount_targets_by_tag_group' && headcountRevision > 0) ||
            !runtime.canUseMaterializedBundle ||
            !hasMaterializedNode
          ),
          filters: runtime.effectiveFilters || {},
          groupBy: runtime.activeGroupBy || '',
          revision: shouldRefreshLive
            ? `${RECRUITMENT_QUERY_REVISION}:${entry.id}:${entry.id === 'headcount_targets_by_tag_group' ? headcountRevision : 0}`
            : entry.id === 'headcount_targets_by_tag_group'
              ? headcountRevision
              : 0
        }];
      })
    )
  });
  const widgetLibraryCategories = useMemo(
    () =>
      recruitmentAnalyticsLibrary.categories.map((category) => ({
        id: category.id,
        label: copy.library.categories[category.id] || category.label,
        widgets: category.widgets.map((widget) => ({
          id: widget.id,
          label: widget.label,
          defaultGraphLabel: copy.menu.graphs[widget.defaultGraph] || widget.defaultGraph,
          graphCount: widget.supportedGraphs.length,
          isAdded: visibleWidgetIds.includes(widget.id),
          hasReadyData: Boolean(widgetNodes[widget.id] || queriedWidgetNodes[widget.id])
        }))
      })),
    [copy.library.categories, copy.menu.graphs, queriedWidgetNodes, visibleWidgetIds, widgetNodes]
  );
  const isScreenLoading =
    Boolean(companyUID) &&
    (!dashboardReady ||
      remoteFilterLoading ||
      ((widgetLoading || queriedLoading) &&
        !Object.keys(widgetNodes).length &&
        !Object.keys(queriedWidgetNodes).length));

  const widgetModels = useMemo(
    () =>
      widgetEntries.map((entry) => {
        const runtime = widgetRuntimeConfig[entry.id] || {};
        const prefs = runtime.prefs || widgetPrefs[entry.id] || {};
        const activeGroupBy = runtime.activeGroupBy || entry.defaultGroupBy || '';
        const unavailableBundle = createUnavailableAnalyticsBundle({
          title: entry.label,
          annotationItems: [],
          supportedFilters: entry.supportedFilters || [],
          supportedGroupBy: entry.groupByOptions || []
        });
        const hasMaterializedNode = Boolean(widgetNodes[entry.id]);
        const canUseMaterializedBundle = Boolean(runtime.canUseMaterializedBundle) && hasMaterializedNode;
        const forceQueriedBundle =
          LIVE_REFRESH_WIDGET_IDS.has(entry.id) ||
          (entry.id === 'headcount_targets_by_tag_group' && headcountRevision > 0);
        const bundle = !forceQueriedBundle && canUseMaterializedBundle
          ? mergeAnalyticsWidgetNode(widgetNodes[entry.id], unavailableBundle, {
              selectedViewId: entry.supportsGroupBy ? activeGroupBy : '', isRTL
            })
          : queriedWidgetNodes[entry.id]
            ? mergeAnalyticsWidgetNode(queriedWidgetNodes[entry.id], unavailableBundle, {
              selectedViewId: entry.supportsGroupBy ? activeGroupBy : '', isRTL
            })
            : companyUID && BACKEND_WIDGET_IDS.has(entry.id)
              ? unavailableBundle
              : unavailableBundle;
        const normalizedBundle = entry.id === 'offer_waterfall'
          ? sanitizeOfferWaterfallBundle(bundle)
          : entry.id === 'headcount_targets_by_tag_group'
            ? mergeHeadcountPlanIntoBundle(bundle, headcountPlan, headcountPlanLoaded)
            : bundle;
        const compatibleGraphIds = getCompatibleGraphs(entry, normalizedBundle, prefs.size || entry.defaultSize);
        const availableGraphIds = FIXED_GRAPH_WIDGET_IDS.has(entry.id) && compatibleGraphIds.includes(entry.defaultGraph)
          ? [entry.defaultGraph]
          : compatibleGraphIds;
        const activeGraph = availableGraphIds.includes(prefs.activeGraph)
          ? prefs.activeGraph
          : availableGraphIds[0] || '';
        const dataset = activeGraph ? normalizedBundle.datasets[activeGraph] : null;
        const title = normalizedBundle.title || entry.label;
        const annotations = normalizedBundle.annotationItems || [];
        const options = entry.id === 'applicant_traffic_map' ? { copy: copy.trafficMap } : {};
        if (copy.widgetUnavailable?.[entry.id]) {
          options.unavailableMessage = copy.widgetUnavailable[entry.id];
        }
        if (entry.id === 'headcount_targets_by_tag_group') {
          options.unavailableMessage = !headcountPlanLoaded
            ? copy.targetLoading
            : (isRTL ? 'لا توجد أهداف توظيف مكونة بعد.' : 'No configured headcount targets yet.');
        }

        return createVisualModel({
          id: entry.id,
          type: entry.visualType,
          size: prefs.size || entry.defaultSize,
          variant: prefs.variant || entry.defaultVariant,
          datasets: normalizedBundle.datasets,
          datasetShape: dataset?.shape || '',
          dataset,
          activeGraph,
          availableGraphs: availableGraphIds.map((graphId) => ({
            id: graphId,
            label: copy.menu.graphs[graphId] || graphId
          })),
          dataStatus:
            normalizedBundle.dataStatus === ANALYTICS_DATA_STATUS.READY && dataset
              ? ANALYTICS_DATA_STATUS.READY
              : normalizedBundle.dataStatus,
          title,
          annotations,
          meta: {
            dataSemantics: entry.dataSemantics,
            graphConfig: entry.graphConfig,
            activeGroupBy,
            supportedGroupBy: normalizedBundle.supportedGroupBy || entry.groupByOptions || []
          },
          options
        });
      }),
    [companyUID, copy.menu.graphs, copy.targetLoading, copy.trafficMap, copy.widgetUnavailable, headcountPlan, headcountPlanLoaded, headcountRevision, isRTL, queriedWidgetNodes, widgetEntries, widgetNodes, widgetPrefs, widgetRuntimeConfig]
  );
  const {
    exporting,
    exportMode,
    handleExportAction
  } = useCanonicalAnalyticsExport({
    screenId: SCREEN_DEFAULTS.screenId,
    companyUID,
    analyticsActorId,
    filterState: queryFilterState,
    widgetModels,
    extraContext: { headcountPlan },
    language: isRTL ? 'ar' : 'en'
  });
  const toolbarActions = useMemo(
    () => getAnalyticsToolbarActions({
      isRTL,
      locked: SCREEN_DEFAULTS.locked,
      allowExport: true,
      exporting,
      exportMode
    }),
    [exportMode, exporting, isRTL]
  );

  const updateWidgetSize = (id, nextSize) => {
    mutateDashboardState((current) => {
      const nextWidgetPrefs = {
        ...current.widgetPrefs,
        [id]: {
          ...(current.widgetPrefs[id] || {}),
          size: nextSize
        }
      };

      const adjacentId = ADJACENT_WIDGETS[id];
      if (adjacentId && nextSize === 'three-quarter') {
        nextWidgetPrefs[adjacentId] = {
          ...(current.widgetPrefs[adjacentId] || {}),
          size: 'compact'
        };
      }

      if (adjacentId && current.widgetPrefs[adjacentId]?.size === 'three-quarter' && nextSize !== 'compact') {
        nextWidgetPrefs[adjacentId] = {
          ...(current.widgetPrefs[adjacentId] || {}),
          size: 'medium'
        };
      }

      return {
        ...current,
        widgetPrefs: nextWidgetPrefs
      };
    });
  };

  const updateChartType = (id, nextGraph) => {
    mutateDashboardState((current) => ({
      ...current,
      widgetPrefs: {
        ...current.widgetPrefs,
        [id]: {
          ...(current.widgetPrefs[id] || {}),
          activeGraph: nextGraph
        }
      }
    }));
  };

  const updateGroupBy = (id, nextGroupBy) => {
    mutateDashboardState((current) => ({
      ...current,
      widgetPrefs: {
        ...current.widgetPrefs,
        [id]: {
          ...(current.widgetPrefs[id] || {}),
          selectorState: {
            ...(current.widgetPrefs[id]?.selectorState || {}),
            groupBy: nextGroupBy
          }
        }
      }
    }));
  };

  const addWidget = (id) => {
    const entry = recruitmentAnalyticsLibrary.widgets.find((widget) => widget.id === id);
    mutateDashboardState((current) => {
      if (current.visibleWidgetIds.includes(id)) return current;

      return {
        visibleWidgetIds: [...current.visibleWidgetIds, id],
        widgetPrefs: {
          ...current.widgetPrefs,
          [id]: current.widgetPrefs[id] || {
            size: entry?.defaultSize || 'medium',
            activeGraph: entry?.defaultGraph || ''
          }
        }
      };
    });
  };

  const handleToolbarAction = (actionId) => {
    if (handleExportAction(actionId)) {
      return;
    }

    if (actionId === 'add_widget') {
      setLibraryOpen(true);
      return;
    }

    if (actionId === 'reset_layout') {
      resetDashboardState();
    }
  };

  return (
    <section className={`analytics-recruitment${isRTL ? ' rtl' : ''}`}>
      {isScreenLoading ? (
        <div className="data-analytics-loading-screen">
          <i className="fa-solid fa-spinner fa-spin" />
          <p>{isRTL ? 'يرجى الانتظار' : 'Please Wait'}</p>
        </div>
      ) : (
        <>
      <RecruitmentFilterBar
        filters={filterModels}
        actions={toolbarActions}
        onAction={handleToolbarAction}
        onFilterChange={setFilterValue}
        isRTL={isRTL}
      />

      <div className="analytics-recruitment-canvas">
        {widgetModels.map((model) => {
          const Renderer = model.dataset ? resolveVisualRenderer(model.activeGraph, model.datasetShape) : null;
          const adjacentId = ADJACENT_WIDGETS[model.id];
          const pairedCompact = adjacentId ? widgetPrefs[adjacentId]?.size === 'three-quarter' : false;
          const groupByOptions = model.id === 'headcount_targets_by_tag_group'
            ? []
            : getDataBackedGroupByOptions({
                localizedGroupByOptions,
                supportedGroupBy: model.meta.supportedGroupBy || [],
                dimensionOptions: recruitmentDimensionOptions
              });
          const chartTypes = HIDDEN_CHART_MENU_WIDGET_IDS.has(model.id) ? [] : model.availableGraphs;
          const headerActions = model.id === 'headcount_targets_by_tag_group'
            ? [{
                id: 'configure_targets',
                label: copy.menu.configureTargets,
                icon: 'fa-sliders',
                onClick: openHeadcountSettings
              }]
            : [];

          return (
            <WidgetShell
              key={model.id}
              id={model.id}
              title={model.title}
              annotationItems={model.annotations}
              size={model.size}
              pairedCompact={pairedCompact}
              variant={model.variant}
              chartTypes={chartTypes}
              activeChartType={model.activeGraph}
              groupByOptions={groupByOptions}
              activeGroupBy={model.meta.activeGroupBy}
              menuCopy={copy.menu}
              headerActions={headerActions}
              onChartTypeChange={updateChartType}
              onGroupByChange={updateGroupBy}
              onSizeChange={updateWidgetSize}
              className={`analytics-recruitment-widget-${model.id}`}
              isRTL={isRTL}
            >
              {Renderer && model.dataStatus === ANALYTICS_DATA_STATUS.READY ? (
                <Renderer
                  model={model}
                  isRTL={isRTL}
                  onChartTypeChange={(graphId) => updateChartType(model.id, graphId)}
                />
              ) : (
                <UnavailableVisual message={model.options?.unavailableMessage || copy.menu.unavailable} />
              )}
            </WidgetShell>
          );
        })}
      </div>

      <WidgetLibraryModal
        isOpen={libraryOpen}
        copy={copy.library}
        categories={widgetLibraryCategories}
        onClose={() => setLibraryOpen(false)}
        onAddWidget={addWidget}
        isRTL={isRTL}
      />
      <HeadcountTargetModal
        copy={copy}
        open={headcountSettingsOpen}
        rows={headcountTargetRows}
        periodLabel={headcountPlan?.periodLabel}
        loading={headcountSettingsLoading}
        saving={headcountSettingsSaving}
        error={headcountSettingsError}
        isRTL={isRTL}
        onClose={() => setHeadcountSettingsOpen(false)}
        onSave={saveHeadcountTargets}
        onAddRow={addHeadcountTargetRow}
        onRemoveRow={removeHeadcountTargetRow}
        onChangeRow={updateHeadcountTargetRow}
      />
        </>
      )}
    </section>
  );
};

export default AnalyticsRecruitment;
